// Chat domain types — mirrors the contract in frontend/docs/chat-api-guide.md.

export type RoomType = 'direct' | 'group';

export type MessageKind = 'text' | 'file' | 'system' | 'card';

/** Structured content for a `kind: 'card'` message (e.g. a course/program invite). */
export interface ChatCardPayload {
  v: number;
  target_type: 'course' | 'program';
  target_id: string;
  title: string;
  description?: string | null;
  image_url?: string | null;
}

export interface ChatRoomMember {
  user_id: string;
  is_admin: boolean;
  joined_at: string;
  // Profile fields are populated by the room-detail endpoint.
  email?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  avatar_url?: string | null;
  role_name?: string | null;
  department_name?: string | null;
  location_name?: string | null;
  is_online?: boolean;
  // Read pointer for this member. ULIDs are time-sortable, so a lexicographic
  // compare against a message id tells us whether the member has seen it.
  last_read_message_id?: string | null;
  last_read_at?: string | null;
}

export interface ChatRoom {
  id: string;
  type: RoomType;
  name: string | null;
  icon_url: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  unread_count: number;
  members: ChatRoomMember[];
  // Caller's own read pointer (the room-detail endpoint surfaces this at the
  // top level for convenience).
  last_read_message_id?: string | null;
  last_read_at?: string | null;
  // Backend-computed peer label (direct rooms) / group label, calculated
  // from the caller's perspective. Use these instead of recomputing on the
  // client when available.
  display_name?: string | null;
  display_avatar?: string | null;
}

export interface ChatAttachment {
  id: string;
  key: string;
  url: string | null;
  message_id: string | null;
  original_name: string;
  mime_type: string;
  size: number;
  uploaded_by: string;
  created_at: string | null;
}

export interface ChatMessage {
  id: string;
  room_id: string;
  sender_id: string;
  body: string | null;
  kind: MessageKind;
  attachment_ids: string[];
  // Structured content for `kind: 'card'` messages; null/absent otherwise.
  payload?: ChatCardPayload | null;
  edited_at: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  // Optional client-only fields used during optimistic send. Never serialized
  // back to the server.
  pending?: boolean;
  send_error?: string;
}

export interface UnreadRoomSummary {
  room_id: string;
  unread_count: number;
  latest_unread_preview: string | null;
}

export interface ChatUnreadSummary {
  total_unread: number;
  rooms: UnreadRoomSummary[];
}

// Tenant directory member returned by GET /tenant/members.
export interface TenantMember {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  avatar_url: string | null;
  role: string;
  department_id: string | null;
  department_name: string | null;
  location_id: string | null;
  location_name: string | null;
  is_online?: boolean;
}

// REST request bodies.
export interface CreateRoomRequest {
  type: RoomType;
  member_user_ids: string[];
  name?: string;
  icon_url?: string;
}

export interface UpdateRoomRequest {
  name?: string | null;
  icon_url?: string | null;
}

export interface SendMessageRequest {
  body?: string;
  attachment_ids?: string[];
}

export interface EditMessageRequest {
  body: string;
}

export interface MarkReadRequest {
  message_id?: string;
}

// Pagination params.
export interface ListRoomsParams {
  type?: RoomType;
  unread_only?: boolean;
  page?: number;
  per_page?: number;
}

export interface ListMessagesParams {
  before?: string;
  after?: string;
  limit?: number;
}

export interface SearchMembersParams {
  search?: string;
  role?: string;
  department_id?: string;
  location_id?: string;
  include_self?: boolean;
  page?: number;
  per_page?: number;
}

// Socket.IO event payloads.
export interface MessageCreatedPayload {
  room_id: string;
  message: ChatMessage;
}

export interface MessageUpdatedPayload {
  message: ChatMessage;
}

export interface MessageDeletedPayload {
  message_id: string;
  room_id: string;
}

export interface RoomMemberAddedPayload {
  room_id: string;
  user_id: string;
  member: TenantMember;
}

export interface RoomMemberRemovedPayload {
  room_id: string;
  user_id: string;
}

export interface RoomUpdatedPayload {
  room: ChatRoom;
}

export interface ReadPayload {
  room_id: string;
  last_read_message_id: string | null;
}

export interface UnreadUpdatedPayload {
  room_id: string;
  unread_count: number;
  latest_unread_preview: string | null;
  total_unread: number;
}

export const CHAT_EVENTS = {
  MESSAGE_CREATED: 'chat:message.created',
  MESSAGE_UPDATED: 'chat:message.updated',
  MESSAGE_DELETED: 'chat:message.deleted',
  ROOM_MEMBER_ADDED: 'chat:room.member_added',
  ROOM_MEMBER_REMOVED: 'chat:room.member_removed',
  ROOM_UPDATED: 'chat:room.updated',
  READ: 'chat:read',
  UNREAD_UPDATED: 'chat:unread.updated',
} as const;
